import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  ChevronLeft,
  Mail,
  Phone,
  MapPin,
  Copy,
  Send,
  RefreshCw,
  Trash2,
  Plus,
  FileText,
  Hammer,
  Ban,
  Link2,
  Check,
  Pencil,
  X,
  CalendarPlus,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";
import { TagsInput } from "@/components/TagsInput";
import { PhonesManager, KindToggle } from "@/components/PhonesManager";
import { ProjectCreateModal } from "@/components/ProjectCreateModal";

const STAGE_LABELS = {
  en_attente: "En attente",
  contacte: "Contacté",
  rdv_fixe: "RDV fixé",
  devis_envoye: "Devis envoyé",
  en_negociation: "En négociation",
  signe: "Signé",
  perdu: "Perdu",
};
const STAGE_COLOR = {
  en_attente: "bg-[#E8DCC8]/15 text-[#E8DCC8] border-[#E8DCC8]/30",
  contacte: "bg-[#3D362E] text-[#F5EFE7] border-[#4E453B]",
  rdv_fixe: "bg-[#D99A2B]/15 text-[#D99A2B] border-[#D99A2B]/30",
  devis_envoye: "bg-[#C4553B]/15 text-[#E07A62] border-[#C4553B]/30",
  en_negociation: "bg-[#8B5CF6]/15 text-[#C4B5FD] border-[#8B5CF6]/40",
  signe: "bg-[#7A8450]/20 text-[#B8C784] border-[#7A8450]/40",
  perdu: "bg-[#A63D2F]/15 text-[#E67B6D] border-[#A63D2F]/30",
};

const INTERACTION_ICON = {
  webhook_recu: Link2,
  webhook_resubmission: RefreshCw,
  webhook_new_project: Plus,
  note: FileText,
  email: Mail,
  appel: Phone,
  sms: Send,
  devis_envoye: FileText,
  devis_signe: Check,
  rdv_pris: Hammer,
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `il y a ${days} j`;
  return d.toLocaleDateString("fr-BE");
}

function initials(c) {
  const a = (c.first_name || "").trim();
  const b = (c.last_name || "").trim();
  if (a || b) return `${a[0] || ""}${b[0] || ""}`.toUpperCase();
  return (c.email || "?")[0]?.toUpperCase() || "?";
}

function formatPhoneBE(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/[^\d+]/g, "");
  const digits = s.startsWith("+32") ? "0" + s.slice(3) : s;
  const m = digits.match(/^(0\d{3})(\d{2})(\d{2})(\d{2})$/);
  return m ? `${m[1]} ${m[2]} ${m[3]} ${m[4]}` : raw;
}

// --- Editable coord field -------------------------------------------------
function EditableField({ label, value, icon: Icon, onSave, type = "text", placeholder, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(value || ""), [value]);
  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };
  const save = async () => {
    if (draft === (value || "")) return setEditing(false);
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="group flex items-start gap-2 border-b border-slate-800/60 py-2 last:border-b-0">
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />}
      <div className="min-w-0 flex-1">
        <Label className="block text-[10px] uppercase tracking-wide text-slate-500">{label}</Label>
        {editing ? (
          <div className="mt-1 flex gap-1">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              type={type}
              placeholder={placeholder}
              className="h-8 border-slate-700 bg-slate-950/50 text-sm text-slate-100"
              data-testid={`${testid}-input`}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") cancel();
              }}
              autoFocus
            />
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="h-8 bg-[#C4553B] px-2 text-white hover:bg-[#B44A32]"
              data-testid={`${testid}-save`}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={cancel}
              className="h-8 border-slate-700 bg-slate-800/50 px-2 text-slate-300"
              data-testid={`${testid}-cancel`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="mt-0.5 flex w-full items-center justify-between rounded px-1 text-left text-sm text-slate-200 hover:bg-slate-800/40"
            data-testid={testid}
          >
            <span className={value ? "" : "italic text-slate-500"}>
              {value || placeholder || "—"}
            </span>
            <Pencil className="ml-2 h-3 w-3 shrink-0 text-slate-600 opacity-0 group-hover:opacity-100" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ContactDetailPage() {
  const { contactId } = useParams();
  const nav = useNavigate();
  const { state } = useAuth();
  const isAdmin = hasRole(state?.user, ["admin"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [contact, setContact] = useState(null);
  const [projects, setProjects] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [hubUrl, setHubUrl] = useState(null);
  const [copiedHub, setCopiedHub] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const projectsRef = useRef(null);

  const reload = () => {
    setLoading(true);
    return api
      .get(`/contacts/${contactId}`)
      .then((r) => {
        setContact(r.data.contact);
        setProjects(r.data.projects || []);
        setTimeline(r.data.timeline || []);
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        nav("/contacts");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
     
  }, [contactId]);

  // Query param ?create=1 → open project modal
  useEffect(() => {
    if (searchParams.get("create") === "1" && contact) {
      setShowProjectModal(true);
      searchParams.delete("create");
      setSearchParams(searchParams, { replace: true });
    }
     
  }, [contact]);

  const patchContact = async (patch) => {
    // BLOC 11/10 14h10 — Fix 2 : try/catch pour éviter tout overlay webpack.
    // Toast FR sur erreur, mais on **re-throw** pour que PhonesManager
    // puisse préserver sa saisie et restaurer le focus.
    try {
      const { data } = await api.patch(`/contacts/${contactId}`, patch);
      setContact(data);
      toast.success("Contact mis à jour");
      return data;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 422) {
        // Toast déjà affiché par PhonesManager pour les numéros ; ici fallback
        // sur erreur plus générale si le champ invalide n'est pas un phone.
        const detail = e?.response?.data?.detail;
        const isPhoneError = Array.isArray(detail) && detail.some(d => (d.loc || []).some(x => x === "phones" || x === "e164"));
        if (!isPhoneError) toast.error(formatApiError(e));
      } else {
        toast.error(formatApiError(e));
      }
      throw e;
    }
  };

  const handleTagsChange = async (tags) => {
    try {
      const { data } = await api.patch(`/contacts/${contactId}`, { tags });
      setContact(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openHubModal = async () => {
    try {
      // On tape sur /api/leads/{first_project.id}/hub qui gère déjà la logique
      // Alternative propre : /api/contacts/{id}/hub — mais garder simple pour 9bis-b.
      // On construit l'URL directement à partir du hub_token du contact (côté back : contact.hub_token).
      // Pour éviter d'exposer le token dans le CRUD list, on interroge /api/contacts/{id} qui l'expose implicitement dans /leads/hub.
      const first = projects[0];
      if (!first) {
        toast.error("Créez d'abord un projet pour générer l'espace client");
        return;
      }
      const { data } = await api.get(`/leads/${first.id}/hub`);
      // Le backend retourne { hub_url, hub_token } — voir hub_routes.py L563.
      // Non-régression P0 iPad 2026-08-29 : lire `hub_url`, pas `url`
      // (l'ancien mapping vers `data.url` faisait un `undefined` silencieux
      // → clic sans effet visible).
      setHubUrl(data.hub_url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyHub = async () => {
    if (!hubUrl) return;
    try {
      await navigator.clipboard.writeText(hubUrl);
      setCopiedHub(true);
      setTimeout(() => setCopiedHub(false), 2000);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const unsubMarketing = async () => {
    if (!window.confirm("Marquer ce contact comme désinscrit du marketing ? Il ne recevra plus d'emails marketing.")) return;
    try {
      await api.patch(`/contacts/${contactId}`, { consent_marketing_granted: false });
      toast.success("Consentement retiré (à finaliser côté backend en 9bis-d)");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteContact = async () => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success("Contact supprimé");
      nav("/contacts");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const hasProjects = projects.length > 0;
  const canDelete = isAdmin && !hasProjects;

  // Documents groupés par projet
  const documentsByProject = useMemo(() => {
    // Pour chaque projet, on essaie de récupérer devis/factures via /api/projects/{id}
    // (déjà exposés côté fiche projet). Pour éviter du fanout ici on affichera un
    // simple compteur + lien "Voir documents" vers la page projet.
    return projects;
  }, [projects]);

  if (loading || !contact) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }

  const displayName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "Contact sans nom";

  return (
    <div className="space-y-5 pb-8" data-testid="contact-detail-page">
      <Link to="/contacts" className="inline-flex items-center gap-1 text-sm text-[#E07A62] hover:underline">
        <ChevronLeft className="h-4 w-4" /> Retour aux contacts
      </Link>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* --- LEFT STICKY COLUMN --- */}
        <div className="space-y-4 lg:sticky lg:top-4 lg:h-fit">
          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="p-5">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#C4553B]/20 text-lg font-semibold text-[#E07A62]">
                  {initials(contact)}
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-100" data-testid="contact-name">
                    {displayName}
                  </h1>
                  {contact.company_name && (
                    <p className="text-xs text-slate-500">{contact.company_name}</p>
                  )}
                </div>
              </div>

              {contact.resubmission_count > 1 && (
                <Badge
                  variant="outline"
                  className="mb-3 border-amber-500/60 bg-amber-500/15 text-xs text-amber-200"
                  data-testid="contact-resubmission-badge"
                >
                  {contact.resubmission_count}× soumissions webhook
                </Badge>
              )}

              <div className="mb-3">
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Tags</Label>
                <div className="mt-1">
                  <TagsInput
                    value={contact.tags || []}
                    onChange={handleTagsChange}
                    placeholder="Ajouter un tag…"
                  />
                </div>
              </div>

              <div className="mt-3">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Coordonnées</h3>
                <EditableField
                  label="Email"
                  value={contact.email}
                  icon={Mail}
                  type="email"
                  placeholder="exemple@domaine.be"
                  testid="edit-email"
                  onSave={(v) => patchContact({ email: v.trim() || null })}
                />
                <EditableField
                  label="Téléphone"
                  value={contact.phone}
                  icon={Phone}
                  placeholder="0470 12 34 56"
                  testid="edit-phone"
                  onSave={(v) => patchContact({ phone: v.trim() })}
                />
                {/* BLOC 11/10 11h45 + 12h20 — Toggle particulier/société */}
                <div className="rounded border border-slate-800 bg-slate-950/40 p-3 space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Type de contact
                  </div>
                  <KindToggle
                    value={contact.kind || "particulier"}
                    onChange={(k) => patchContact({ kind: k })}
                  />
                  <div className="text-xs text-slate-500">
                    Pilote l'affichage iOS : un numéro fixe sera noté « Travail »
                    pour une société, « Domicile » pour un particulier.
                  </div>
                </div>
                {/* B2bis — Statut explicite client/prospect (CardDAV X-ABLabel) */}
                <div className="rounded border border-slate-800 bg-slate-950/40 p-3 space-y-2" data-testid="contact-status-section">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Statut commercial
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="contact-status-client-btn"
                      onClick={() => patchContact({ status: "client" })}
                      className={`flex-1 rounded border px-3 py-2 text-sm font-medium transition ${
                        contact.status === "client"
                          ? "border-emerald-500 bg-emerald-500/15 text-emerald-200"
                          : "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
                      }`}
                    >
                      Client
                    </button>
                    <button
                      type="button"
                      data-testid="contact-status-prospect-btn"
                      onClick={() => patchContact({ status: "prospect" })}
                      className={`flex-1 rounded border px-3 py-2 text-sm font-medium transition ${
                        contact.status === "prospect"
                          ? "border-sky-500 bg-sky-500/15 text-sky-200"
                          : "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
                      }`}
                    >
                      Prospect
                    </button>
                    <button
                      type="button"
                      data-testid="contact-status-clear-btn"
                      onClick={() => patchContact({ status: null })}
                      disabled={!contact.status}
                      className={`rounded border px-3 py-2 text-sm font-medium transition ${
                        contact.status
                          ? "border-slate-700 bg-slate-900 text-slate-400 hover:bg-slate-800"
                          : "border-slate-800 bg-slate-900/50 text-slate-600 cursor-not-allowed"
                      }`}
                      aria-label="Effacer le statut"
                    >
                      —
                    </button>
                  </div>
                  <div className="text-xs text-slate-500">
                    Affiché sur l'appel entrant iPhone (si pas d'historique métier :
                    devis, chantier, facture — qui ont priorité).
                  </div>
                </div>
                {/* BLOC 11/10 11h45 + 12h20 — Numéros supplémentaires */}
                <div className="rounded border border-slate-800 bg-slate-950/40 p-3 space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Numéros supplémentaires (identification des appels)
                  </div>
                  <PhonesManager
                    value={contact.phones || []}
                    onChange={(next) => patchContact({ phones: next })}
                  />
                </div>
                <EditableField
                  label="Rue et n°"
                  value={contact.address_street}
                  icon={MapPin}
                  testid="edit-street"
                  onSave={(v) => patchContact({ address_street: v.trim() })}
                />
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <EditableField
                    label="CP"
                    value={contact.address_zip}
                    testid="edit-zip"
                    onSave={(v) => patchContact({ address_zip: v.trim() })}
                  />
                  <EditableField
                    label="Ville"
                    value={contact.address_city}
                    testid="edit-city"
                    onSave={(v) => patchContact({ address_city: v.trim() })}
                  />
                </div>
                <EditableField
                  label="N° TVA"
                  value={contact.vat_number}
                  testid="edit-vat"
                  placeholder="BE0123456789"
                  onSave={(v) => patchContact({ vat_number: v.trim().toUpperCase().replace(/[.\s]/g, "") || null })}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="space-y-2 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Espace client</h3>
              {hubUrl ? (
                <div className="space-y-2">
                  <div className="rounded border border-slate-800 bg-slate-950/60 px-2 py-1.5 text-xs text-slate-300 break-all">
                    {hubUrl}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyHub}
                      className="flex-1 border-slate-700 bg-slate-800/50 text-slate-200"
                      data-testid="hub-copy-btn"
                    >
                      {copiedHub ? <Check className="mr-2 h-3.5 w-3.5" /> : <Copy className="mr-2 h-3.5 w-3.5" />}
                      {copiedHub ? "Copié" : "Copier"}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openHubModal}
                  disabled={!hasProjects}
                  className="w-full border-slate-700 bg-slate-800/50 text-slate-200"
                  data-testid="hub-open-btn"
                >
                  <Link2 className="mr-2 h-3.5 w-3.5" />
                  {hasProjects ? "Afficher le lien" : "Aucun projet"}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="space-y-2 p-4">
              {contact.consent_marketing?.granted && !contact.marketing_unsubscribed && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={unsubMarketing}
                  className="w-full border-amber-800/60 bg-amber-900/20 text-amber-200 hover:bg-amber-900/40"
                  data-testid="unsub-marketing-btn"
                >
                  <Ban className="mr-2 h-3.5 w-3.5" /> Désinscrire du marketing
                </Button>
              )}
              {contact.marketing_unsubscribed && (
                <Badge variant="outline" className="border-slate-700 bg-slate-800/50 text-slate-400">
                  Désinscrit du marketing
                </Badge>
              )}
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={!canDelete}
                  className="w-full border-red-900/50 bg-red-900/10 text-red-300 hover:bg-red-900/30 disabled:opacity-50"
                  data-testid="btn-delete-contact"
                  title={canDelete ? "" : "Impossible : projets rattachés"}
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" /> Supprimer le contact
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* --- RIGHT COLUMN --- */}
        <div className="space-y-5 lg:col-span-2">
          {/* Projets */}
          <Card className="border-slate-800 bg-slate-900/40" ref={projectsRef}>
            <CardContent className="p-4">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-100">
                  Projets <span className="ml-1 text-sm text-slate-500">({projects.length})</span>
                </h2>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Turn 2b UX-C : accès rapide RDV depuis la fiche contact
                      const openProjects = (projects || []).filter((p) => !p.closed);
                      if (openProjects.length === 0) {
                        toast.info("Crée d'abord un projet à ce contact pour y fixer un RDV.");
                        setShowProjectModal(true);
                        return;
                      }
                      if (openProjects.length === 1) {
                        nav(`/projects/${openProjects[0].id}?open_rdv=1`);
                        return;
                      }
                      // Plusieurs projets ouverts → simple prompt
                      const label = openProjects
                        .map((p, i) => `${i + 1}. ${p.title || `Projet ${i + 1}`}`)
                        .join("\n");
                      const raw = window.prompt(
                        `Ce contact a ${openProjects.length} projets ouverts. Quel projet ?\n\n${label}\n\nEntre le numéro :`
                      );
                      const idx = Number(raw) - 1;
                      if (Number.isInteger(idx) && openProjects[idx]) {
                        nav(`/projects/${openProjects[idx].id}?open_rdv=1`);
                      }
                    }}
                    className="border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                    data-testid="btn-new-rdv-from-contact"
                  >
                    <CalendarPlus className="mr-2 h-3.5 w-3.5" /> Nouveau RDV
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowProjectModal(true)}
                    className="bg-[#C4553B] text-white hover:bg-[#B44A32]"
                    data-testid="btn-new-project"
                  >
                    <Plus className="mr-2 h-3.5 w-3.5" /> Nouveau projet
                  </Button>
                </div>
              </div>
              {projects.length === 0 ? (
                <div className="rounded border border-dashed border-slate-800 py-8 text-center text-sm text-slate-500">
                  Aucun projet rattaché à ce contact.
                  <br />
                  <button
                    onClick={() => setShowProjectModal(true)}
                    className="mt-2 text-[#E07A62] hover:underline"
                  >
                    Créer un premier projet
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {projects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => nav(`/projects/${p.id}`)}
                      className="flex w-full flex-col gap-1.5 rounded border border-slate-800 bg-slate-950/40 p-3 text-left transition hover:border-[#C4553B]/60 hover:bg-slate-900/60"
                      data-testid={`project-card-${p.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-slate-100">{p.title || "Projet sans titre"}</span>
                        <Badge className={`${STAGE_COLOR[p.stage] || "bg-slate-800 text-slate-300"} border shrink-0`}>
                          {STAGE_LABELS[p.stage] || p.stage}
                        </Badge>
                      </div>
                      {p.project_description && (
                        <p className="line-clamp-2 text-xs text-slate-400">{p.project_description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                        <span>Créé {relTime(p.created_at)}</span>
                        {p.stage_updated_at && <span>· MAJ {relTime(p.stage_updated_at)}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents groupés par projet */}
          {projects.length > 0 && (
            <Card className="border-slate-800 bg-slate-900/40">
              <CardContent className="p-4">
                <h2 className="mb-3 text-lg font-semibold text-slate-100">Documents</h2>
                <div className="space-y-3">
                  {documentsByProject.map((p) => (
                    <ProjectDocumentsBlock key={p.id} project={p} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline consolidée */}
          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="p-4">
              <h2 className="mb-3 text-lg font-semibold text-slate-100">
                Timeline consolidée <span className="ml-1 text-sm text-slate-500">({timeline.length})</span>
              </h2>
              {timeline.length === 0 ? (
                <p className="text-sm text-slate-500">Aucune interaction pour l'instant.</p>
              ) : (
                <div className="space-y-2">
                  {timeline.map((it) => {
                    const Icon = INTERACTION_ICON[it.type] || FileText;
                    const proj = projects.find((p) => p.id === it.project_id);
                    return (
                      <div
                        key={it.id}
                        className="flex gap-3 rounded border border-slate-800 bg-slate-950/40 p-3"
                        data-testid={`timeline-item-${it.id}`}
                      >
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                            <span className="font-medium text-slate-300">{it.type.replace(/_/g, " ")}</span>
                            {proj && (
                              <Badge
                                variant="outline"
                                className="border-slate-700 bg-slate-800 text-[10px] text-slate-400"
                              >
                                {proj.title || "Projet"}
                              </Badge>
                            )}
                            <span className="ml-auto">{relTime(it.created_at)}</span>
                          </div>
                          {it.content && <p className="mt-1 text-sm text-slate-200">{it.content}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* --- Modals --- */}
      <ProjectCreateModal
        open={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        contactId={contact.id}
        contactName={displayName}
        onCreated={(_p) => {
          setShowProjectModal(false);
          reload();
        }}
      />

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md border-slate-800 bg-[#1E1A17] text-slate-100" data-testid="delete-contact-modal">
          <DialogHeader>
            <DialogTitle>Supprimer le contact</DialogTitle>
            <DialogDescription className="text-slate-400">
              Cette action est irréversible. Le contact <span className="text-slate-100">{displayName}</span> sera archivé (soft delete).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="border-slate-700 bg-slate-800/50 text-slate-200">
              Annuler
            </Button>
            <Button
              onClick={deleteContact}
              className="bg-red-700 text-white hover:bg-red-600"
              data-testid="confirm-delete-contact"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sub-block : documents d'un projet (fetch lazy)
function ProjectDocumentsBlock({ project }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    setOpen((v) => !v);
    if (!detail && !loading) {
      setLoading(true);
      try {
        const { data } = await api.get(`/projects/${project.id}`);
        setDetail(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="rounded border border-slate-800 bg-slate-950/40" data-testid={`docs-project-${project.id}`}>
      <button
        onClick={toggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-800/30"
      >
        <span className="flex items-center gap-2 text-slate-200">
          <FileText className="h-3.5 w-3.5 text-slate-500" />
          <span className="font-medium">{project.title || "Projet"}</span>
          <Badge className={`${STAGE_COLOR[project.stage] || "bg-slate-800 text-slate-300"} border text-[10px]`}>
            {STAGE_LABELS[project.stage] || project.stage}
          </Badge>
        </span>
        <span className="text-xs text-slate-500">{open ? "Masquer" : "Afficher"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-800 p-3 text-sm">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          ) : detail ? (
            <div className="space-y-2 text-slate-300">
              {detail.chantiers?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Chantiers</p>
                  {detail.chantiers.map((c) => (
                    <Link
                      key={c.id}
                      to={`/chantiers/${c.id}`}
                      className="mt-0.5 flex items-center gap-2 text-slate-200 hover:text-[#E07A62]"
                    >
                      <Hammer className="h-3.5 w-3.5" /> {c.name || "Chantier"} <span className="text-xs text-slate-500">· {c.status}</span>
                    </Link>
                  ))}
                </div>
              )}
              {!detail.chantiers?.length && (
                <p className="text-xs text-slate-500">
                  Aucun document listé ici. Consultez la fiche projet pour devis & factures.
                </p>
              )}
              <Link
                to={`/projects/${project.id}`}
                className="mt-2 inline-flex items-center gap-1 text-xs text-[#E07A62] hover:underline"
              >
                Ouvrir la fiche projet →
              </Link>
            </div>
          ) : (
            <p className="text-xs text-slate-500">Chargement…</p>
          )}
        </div>
      )}
    </div>
  );
}
